import { Box, Avatar } from "@mui/material";
import { Typography, SelectableCard } from "@powerdiary/components-library/lib";
import styles from "./ListItem.module.css";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { amountToCurrency } from "utils/localeHelper";

interface ListItemProps {
    title: string;
    description?: string;
    image?: string;
    duration?: string;
    price?: number;
    selected?: boolean;
    onClick?: () => void;
    showAvatar?: boolean;
    dataTestIdSuffix: string;
}

export default function ListItem({
    title,
    description,
    image,
    duration,
    price,
    selected = false,
    onClick,
    showAvatar = false,
    dataTestIdSuffix,
}: Readonly<ListItemProps>) {
    const {
        clientPortalConfig: { currencySymbol },
    } = useClientPortalConfigContext();

    const avatarInitials = title
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase();

    const options = [
        {
            content: (
                <Box className={styles.wrapper} data-test-id={`list_item_wrapper_${dataTestIdSuffix}`}>
                    {showAvatar && (
                        <Avatar
                            src={image || undefined}
                            alt={title}
                            className={styles.avatar}
                            data-test-id={`list_item_avatar_${dataTestIdSuffix}`}
                        >
                            {avatarInitials}
                        </Avatar>
                    )}

                    <Box className={styles.content} data-test-id="list_item_content">
                        <Typography
                            variant="paragraphMediumSemiBold"
                            data-test-id={`text_list_item_title_${dataTestIdSuffix}`}
                        >
                            {title}
                        </Typography>

                        {
                            <Box className={styles.serviceDetails}>
                                {description && (
                                    <Typography
                                        variant="paragraphSmallRegular"
                                        className={styles.description}
                                        data-test-id={`list_item_description_${dataTestIdSuffix}`}
                                    >
                                        {description}
                                    </Typography>
                                )}

                                <Box className={styles.servicePriceDuration}>
                                    {price > 0 && (
                                        <Typography
                                            variant="paragraphSmallRegular"
                                            data-test-id={`text_list_item_price_${dataTestIdSuffix}`}
                                        >
                                            {amountToCurrency(price, currencySymbol)}
                                        </Typography>
                                    )}

                                    {price > 0 && duration && (
                                        <Typography variant="paragraphSmallRegular" className={styles.pipe}>
                                            |
                                        </Typography>
                                    )}

                                    {duration && (
                                        <Typography
                                            variant="paragraphSmallRegular"
                                            data-test-id={`text_list_item_duration_${dataTestIdSuffix}`}
                                        >
                                            {duration}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        }
                    </Box>
                </Box>
            ),
            value: title,
        },
    ];

    return (
        <SelectableCard
            key={`${title}-${selected}`}
            options={options}
            onChange={() => onClick?.()}
            value={selected ? title : null}
            dataTestIdSuffix={dataTestIdSuffix}
        />
    );
}
